import type {
  CreditScoreRange,
  AnnualIncome,
  MostRecentEviction,
  PropertySeeker,
} from "@/.zenstack/logical-prisma-client";
import { Prisma } from "@/.zenstack/logical-prisma-client";

const _tenantScreeningCriteriaCheckArgs =
  Prisma.validator<Prisma.TenantScreeningCriteriaDefaultArgs>()({
    select: {
      id: true,
      isEnabled: true,
      creditScoreRange: true,
      annualIncome: true,
      mostRecentEviction: true,
    },
  });

export type TenantScreeningCriteriaCheckProps =
  Prisma.TenantScreeningCriteriaGetPayload<
    typeof _tenantScreeningCriteriaCheckArgs
  >;

const CREDIT_ORDER: Record<CreditScoreRange, number> = {
  NOT_PROVIDED: 0,
  NOT_REQUIRED: 0,
  POOR: 1,
  FAIR: 2,
  GOOD: 3,
  VERY_GOOD: 4,
  EXCELLENT: 5,
};
const INCOME_ORDER: Record<AnnualIncome, number> = {
  NOT_PROVIDED: 0,
  NOT_REQUIRED: 0,
  UNDER_25000: 1,
  FROM_25000_TO_49999: 2,
  FROM_50000_TO_74999: 3,
  FROM_75000_TO_99999: 4,
  FROM_100000_TO_149999: 5,
  MORE_THAN_150000: 6,
};
const EVICTION_ORDER: Record<MostRecentEviction, number> = {
  NOT_PROVIDED: 0,
  NOT_REQUIRED: 0,
  LESS_THAN_3_YEARS_AGO: 1,
  BETWEEN_3_AND_7_YEARS_AGO: 2,
  MORE_THAN_7_YEARS_AGO: 3,
  NEVER: 4,
};

/**
 * Compare tenant profile against listing screening criteria.
 * Returns true if the tenant meets the criteria, false otherwise.
 * Also returns an array of reasons why the tenant did not meet the criteria.
 */
export function checkTenantScreeningCriteria(
  criteria: TenantScreeningCriteriaCheckProps | null | undefined,
  tenant:
    | Pick<
        PropertySeeker,
        "creditScoreRange" | "annualIncome" | "mostRecentEviction"
      >
    | null
    | undefined,
): { screeningPassed: boolean; reason: string[] } {
  // If there is no criteria or it is not enabled, pass
  if (!criteria || criteria.isEnabled === false)
    return { screeningPassed: true, reason: [] };

  // If there is no tenant, fail
  if (!tenant)
    return {
      screeningPassed: false,
      reason: ["No tenant screening criteria provided."],
    };

  // Check each field, if criteria is NOT_REQUIRED, pass
  const reasons: string[] = [];
  const creditOk =
    criteria.creditScoreRange === "NOT_REQUIRED" ||
    CREDIT_ORDER[tenant.creditScoreRange] >=
      CREDIT_ORDER[criteria.creditScoreRange];
  if (!creditOk)
    reasons.push("Tenant credit score does not meet screening criteria.");
  const incomeOk =
    criteria.annualIncome === "NOT_REQUIRED" ||
    INCOME_ORDER[tenant.annualIncome] >= INCOME_ORDER[criteria.annualIncome];
  if (!incomeOk)
    reasons.push("Tenant annual income does not meet screening criteria.");
  const evictionOk =
    criteria.mostRecentEviction === "NOT_REQUIRED" ||
    EVICTION_ORDER[tenant.mostRecentEviction] >=
      EVICTION_ORDER[criteria.mostRecentEviction];

  return {
    screeningPassed: creditOk && incomeOk && evictionOk,
    reason: creditOk && incomeOk && evictionOk ? [] : reasons,
  };
}
